<template>
  <div>
    <p
      v-if="failedToInitialize"
      class="message message--danger"
    >
      Oops! Our payment form failed to load. Please try again later.
    </p>

    <template v-else>
      <text-input
        id="frmNameCC"
        v-model="nameOnCard"
        type="text"
        label="Name on Card"
        name="ccname"
        autocomplete="cc-name"
        :validations="{
          full_name: true,
          required: !skipRequiredValidation
        }"
        @focus="focusHandler"
      />
    </template>

    <div
      v-show="!failedToInitialize"
      class="text-input text-input--stripe"
    >
      <div
        id="stripe-card"
        class="text-input__input"
      />
    </div>
  </div>
</template>



<script>
  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';



  let card;
  let stripe;



  export default {
    name: 'CardInformationStripe',

    inject: {
      $validator: {
        from: '$validator'
      }
    },

    props: {
      paymentGateway: {
        type: Object,
        required: true
      },

      skipRequiredValidation: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        failedToInitialize: false,
        nameOnCard: null
      };
    },

    unmounted() {
      this.destroyedHandler();
    },

    mounted() {
      this.mountedHandler();
    },

    methods: {
      destroyedHandler() {
        if (card) {
          card.destroy();
        }
      },

      focusHandler() {
        this.$emit('focus');
      },

      handleStripeSubmitError(result) {
        this.$emit('error', result.error);

        events.emit('notification', {
          type: 'error',
          message: result.error.message,
          clickToDismiss: true
        });
      },

      handleStripeSubmitSuccess(result) {
        const { paymentMethod } = result;

        this.$emit('success', {
          paymentMethod: {
            type: 'third-party-form',
            body: {
              billingAddress: {
                postalCode: paymentMethod.billing_details.address.postal_code
              },
              cardNumberLastFour: paymentMethod.card.last4,
              cardType: paymentMethod.card.brand,
              expiration: {
                month: paymentMethod.card.exp_month,
                year: paymentMethod.card.exp_year
              },
              nameOnCard: this.nameOnCard,
              paymentType: 'CreditCard',
              token: paymentMethod.id
            }
          }
        });
      },

      initializeLibrary() {
        try {
          const { connectedAccountId, publishableKey } = this.paymentGateway.providers.Stripe;
          let options;

          if (connectedAccountId) {
            options = {
              stripeAccount: connectedAccountId
            };
          }

          stripe = window.Stripe(publishableKey, options);
          const elements = stripe.elements();

          const computedStyles = getComputedStyle(document.documentElement);
          const dangerColor = computedStyles.getPropertyValue('--color-danger');
          const darkColor = computedStyles.getPropertyValue('--color-dark');
          const darkGrayColor = computedStyles.getPropertyValue('--color-dark-gray');
          const primaryBrandColor = computedStyles.getPropertyValue('--color-brand-primary');

          const style = {
            base: {
              iconColor: primaryBrandColor,
              color: darkColor,
              fontFamily: "'Sen', 'Roboto', 'Helvetica Neue', 'Helvetica', sans-serif",
              fontSmoothing: 'antialiased',
              fontSize: '16px',
              '::placeholder': {
                color: darkGrayColor
              }
            },
            invalid: {
              color: dangerColor
            }
          };

          card = elements.create('card', {
            style
          });

          card.mount('#stripe-card');
          card.on('change', this.interactedHandler);
          card.on('focus', this.interactedHandler);
        }

        catch (error) {
          errorHandlers.silent(error);
          this.failedToInitialize = true;
        }
      },

      interactedHandler() {
        this.$emit('interacted');
      },

      mountedHandler() {
        this.initializeLibrary();
      },

      async submit() {
        try {
          const result = await stripe.createPaymentMethod({
            type: 'card',
            card,
            billing_details: {
              name: this.nameOnCard
            }
          });

          if (result.error) {
            this.handleStripeSubmitError(result);
          }

          else {
            this.handleStripeSubmitSuccess(result);
          }
        }

        catch (error) {
          this.$emit('error', error);
        }
      }
    }
  };
</script>
